import React, { useState, useEffect, useContext } from 'react';
import { KeycloakContext } from '../App';
import {
  DocumentTextIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import {
  PageHeader,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  StatusBadge,
  EmptyState,
  ErrorAlert,
  Skeleton,
  Tooltip,
} from '../components/ui';
import { formatDate, formatCurrency } from '../lib/format';

interface Bill {
  id: string;
  bill_number: string;
  date: string;
  due_date: string;
  amount: number;
  balance: number;
  status: string;
  pdf_path?: string;
}

const Bills: React.FC = () => {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const [billsList, setBillsList] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Bill | null>(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/bills`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('Failed to fetch bills list');
      const data = await res.json();
      setBillsList(data || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = async (billId: string) => {
    try {
      const res = await fetch(`${API_URL}/bills/${billId}`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch bill details');
      const data = await res.json();
      setSelected(data);
    } catch (err: any) {
      toast(`Failed to fetch bill details: ${err.message}`, 'error');
    }
  };

  const downloadPDF = async (billId: string, billNumber: string) => {
    try {
      const res = await fetch(`${API_URL}/bills/${billId}/pdf`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!res.ok) throw new Error('Failed to download PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bill_${billNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      toast(`Failed to download PDF: ${err.message}`, 'error');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<DocumentTextIcon />}
        title="Bills"
        description="View and manage your billing history and invoices."
      />

      {error && <ErrorAlert message={error} />}

      {loading ? (
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : billsList.length === 0 ? (
        <EmptyState
          icon={<DocumentTextIcon />}
          title="No bills"
          description="You don't have any bills yet."
        />
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bill #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="hidden md:table-cell">Due date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right hidden sm:table-cell">Balance</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {billsList.map((bill) => (
                <TableRow key={bill.id}>
                  <TableCell className="font-medium text-foreground">{bill.bill_number}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(bill.date)}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">
                    {formatDate(bill.due_date)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(bill.amount)}</TableCell>
                  <TableCell className="hidden sm:table-cell text-right tabular-nums">
                    {formatCurrency(bill.balance)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={bill.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Tooltip content="View details">
                        <Button variant="ghost" size="icon-sm" onClick={() => openDetails(bill.id)}>
                          <EyeIcon />
                        </Button>
                      </Tooltip>
                      {bill.pdf_path && (
                        <Tooltip content="Download PDF">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => downloadPDF(bill.id, bill.bill_number)}
                          >
                            <ArrowDownTrayIcon />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} size="2xl">
        {selected && (
          <>
            <DialogHeader
              title={`Bill ${selected.bill_number}`}
              description={`Issued on ${formatDate(selected.date)}`}
              onClose={() => setSelected(null)}
            />
            <DialogBody>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div>
                  <dt className="text-muted-foreground">Bill number</dt>
                  <dd className="mt-1 font-medium text-foreground">{selected.bill_number}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Status</dt>
                  <dd className="mt-1">
                    <StatusBadge status={selected.status} />
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Date</dt>
                  <dd className="mt-1 text-foreground">{formatDate(selected.date)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Due date</dt>
                  <dd className="mt-1 text-foreground">{formatDate(selected.due_date)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Amount</dt>
                  <dd className="mt-1 text-foreground tabular-nums">{formatCurrency(selected.amount)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Balance</dt>
                  <dd className="mt-1 text-foreground tabular-nums">{formatCurrency(selected.balance)}</dd>
                </div>
              </dl>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelected(null)}>
                Close
              </Button>
              {selected.pdf_path && (
                <Button onClick={() => downloadPDF(selected.id, selected.bill_number)}>
                  <ArrowDownTrayIcon />
                  Download PDF
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default Bills;
